import { ApiMethodKeys, EntityName, SnackbarKeys, notificationsMsgs } from "@sso/constant";
import { closeDialog } from "@sso/store/slices/dialog";
import { pushSnackbar } from "@sso/store/slices/snackbar";
import {
  ApiPageResponse,
  ID,
  Pricing,
  PricingCreateRequest,
  PricingSummary,
  PricingSummaryKeys,
  PricingUpdateRequest,
} from "@sso/types";

import { ssoApi } from "..";
import { CacheKeys } from "../cacheKeys";
import { Endpoints } from "../endpoints";

export const pricingApi = ssoApi.enhanceEndpoints({ addTagTypes: [CacheKeys.PRICING] }).injectEndpoints({
  endpoints: builder => ({
    pricings: builder.query<ApiPageResponse<PricingSummary>, string>({
      query: query => ({ url: `${Endpoints.PRICING}?${query}` }),
      providesTags: [CacheKeys.PRICING],
    }),
    addPricing: builder.mutation<Pricing, PricingCreateRequest>({
      query: data => ({ url: `${Endpoints.PRICING}`, method: ApiMethodKeys.POST, data }),
      invalidatesTags: [CacheKeys.PRICING],
      onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          dispatch(
            pushSnackbar({
              type: SnackbarKeys.SUCCESS,
              title: notificationsMsgs.ADD_ITEM(EntityName.PRICING),
            }),
          );
          dispatch(closeDialog());
        });
      },
    }),
    updatePricing: builder.mutation<Pricing, PricingUpdateRequest>({
      query: data => ({ url: `${Endpoints.PRICING}`, method: ApiMethodKeys.PUT, data }),
      invalidatesTags: [CacheKeys.PRICING],
      onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          dispatch(
            pushSnackbar({
              type: SnackbarKeys.SUCCESS,
              title: notificationsMsgs.ADD_ITEM(EntityName.PRICING),
            }),
          );
          dispatch(closeDialog());
        });
      },
    }),
    deletePricing: builder.mutation<void, string>({
      query: PricingId => ({
        url: Endpoints.PRICING,
        params: { PricingId },
        method: ApiMethodKeys.DELETE,
      }),
      invalidatesTags: [CacheKeys.PRICING],
      onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          dispatch(
            pushSnackbar({
              type: SnackbarKeys.SUCCESS,
              title: notificationsMsgs.DELETE_ITEM(EntityName.PRICING),
            }),
          );
          dispatch(closeDialog());
        });
      },
    }),
    getPricingById: builder.query<Pricing, ID>({
      query: pricingId => ({ url: `${Endpoints.PRICING}/${pricingId}` }),
      transformResponse: (response: { result: Pricing }) => {
        const date = new Date(response.result[PricingSummaryKeys.DATE_OF_EFFECT]);
        const offset = date.getTimezoneOffset();
        date.setMinutes(date.getMinutes() - offset);
        response.result[PricingSummaryKeys.DATE_OF_EFFECT] = date.toISOString().split("T")[0];
        return response.result;
      },
      providesTags: [CacheKeys.PRICING],
    }),
  }),
});

export const {
  usePricingsQuery,
  useAddPricingMutation,
  useUpdatePricingMutation,
  useDeletePricingMutation,
  useGetPricingByIdQuery,
} = pricingApi;
